/**
 * Module: Protected Route Guard
 * SpecReferenceID: AUTH-V1
 * Requirements: AUTH-V1-R06
 * Description: Route wrapper that restricts access based on authentication
 * and optionally by user role. AUTH-V1-R06 requires role-based restriction
 * on Chat, Search, and Admin tools.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Optional roles required for access (AUTH-V1-R06). User must have at least one. */
  requiredRoles?: string[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps): React.ReactElement {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // AUTH-V1-R06: Restrict access based on assigned user roles
  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user?.roles ?? [];
    const hasRequiredRole = requiredRoles.some((role) => userRoles.includes(role));
    if (!hasRequiredRole) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
